import { Component, ErrorHandler, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy, Injector, inject, signal } from '@angular/core'
import { disabled, form } from '@angular/forms/signals'

import { MatInput } from '@angular/material/input'
import { MatSnackBar } from '@angular/material/snack-bar'

import { AsetustenOletuksetService } from '../../_angular/service/asetusten-oletukset.service'
import { LaskuService } from '../../_angular/service/lasku/lasku.service'

import { arvonMuutokset, asetaArvo } from '../../_jaettu-angular/_lomakkeet/arvon-muutokset'
import { FieldErrors, FormValidationService } from '../../_jaettu-angular/service/form-validation.service'
import { LadataanService } from '../../_jaettu-angular/service/ladataan.service'
import { LemonTranslationService } from '../../_jaettu-angular/service/lemon-translation.service'
import { Maa, MaaService } from '../../_jaettu-angular/service/maa.service'

import { Laskuasetukset } from '@lemon/core-firebase/model/lasku'
import { LaskuKopioija } from '@lemon/core-firebase/service/lasku/lasku.kopioija'

import { DateService } from '@lemon/core/service/date.service'
import { KayttajaService } from 'app/_angular/service/kayttaja.service'
import { combineLatest, Observable, Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'

interface ReskontraLomake {
  reskontra: boolean | null
}

@Component({
  selector: 'app-reskontra-asetukset',
  templateUrl: './reskontra-asetukset.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ReskontraAsetuksetComponent implements OnInit, OnDestroy {
  @ViewChild(MatInput, { static: true }) nimiInput: MatInput

  private _ngUnsubscribe: Subject<void> = new Subject<void>()
  private readonly _injector = inject(Injector)

  readonly activationDisabled = signal(false)

  lomake = form(signal<ReskontraLomake>({ reskontra: null }), polku => {
    disabled(polku.reskontra, () => this.activationDisabled())
  })
  fieldErrors: FieldErrors = {}

  asetukset: Laskuasetukset = null
  maat: Observable<Maa[]>

  constructor(
    private _laskuService: LaskuService,
    private _validationService: FormValidationService,
    private _laskuKopioija: LaskuKopioija,
    private _asetuksetOletuksetService: AsetustenOletuksetService,
    private _errorHandler: ErrorHandler,
    private _snackbar: MatSnackBar,
    private _ladataanService: LadataanService,
    private _translationService: LemonTranslationService,
    private _maaService: MaaService,
    private _kayttajaService: KayttajaService,
    private _dateService: DateService,
  ) {
    this.maat = this._maaService.kaikkiMaatObservable
  }

  ngOnInit() {
    arvonMuutokset(this.lomake.reskontra, { injector: this._injector })
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(value => {
        this.asetukset.maksupReskontraActive = value
      })

    // Load data
    combineLatest([this._laskuService.asetuksetObservable, this._kayttajaService.nykyinenAsiakasObservable])
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(([asetukset, asiakas]) => {
        if (asetukset) {
          this.asetukset = this._laskuKopioija.copyAsetukset(asetukset)
          this._asetuksetOletuksetService.poistaOletuksetAsetuksista(this.asetukset)
        } else {
          this.asetukset = {
            avain: null,
            nimi: null,
            ytunnus: null,
            katuosoite: null,
            postitmp: null,
            postinro: null,
            maakoodi: 'FIN',
            date: null,
            replyto: null,
            puhelin: null,
            bic: null,
            iban: null,
            pankki: null,
            base64Logo: null,
            ohjeet: null,
            slogan: null,
            piilotaYrityksenNimiLaskulla: false,
            summahuomautusPiilotettu: false,
            muistutusSpostiasetukset: {},
            spostiasetukset: {},
            tervetuloaNahty: true,
            maksupReskontraActive: true,
          }
        }

        this.asetukset.tervetuloaNahty = true

        const currentLocalDate = this._dateService.currentLocalDate()
        const sahkoinenTilioteKaytossa =
          asiakas?.maksutavat?.some(
            maksutapa =>
              maksutapa.aktiivinen &&
              maksutapa.sahkoisenTiliotteenAlatyyppi &&
              this._dateService.onkoLocalDateKahdenValissa(
                currentLocalDate,
                maksutapa.tilitapahtumatSynkronoidaanAlkaen,
                maksutapa.tilitapahtumatSynkronoidaanLoppuen || { year: 9999, month: 12, day: 31 },
              ),
          ) ?? false

        if (sahkoinenTilioteKaytossa) {
          this.activationDisabled.set(false)
          asetaArvo(this.lomake.reskontra, asetukset?.maksupReskontraActive ?? false)
        } else {
          this.activationDisabled.set(true)
          asetaArvo(this.lomake.reskontra, false)
        }
      })
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  save() {
    if (!this.lomake().valid()) {
      this._validationService.merkitseKokoLomakeKosketuksi(this.lomake)
      return
    }

    this._ladataanService.aloitaLataaminen()
    this._laskuService
      .updateLaskuasetukset(this.asetukset)
      .then(() => {
        const lokalisoitu = this._translationService.lokalisoi('laskuasetukset.tallennettu-onnistuneesti')
        this._snackbar.open(lokalisoitu, 'OK', { duration: 5000, verticalPosition: 'top' })
        this._ladataanService.lopetaLataaminen()
      })
      .catch(err => {
        this._ladataanService.lopetaLataaminen()
        this._errorHandler.handleError(err)
      })
  }
}
